import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import {
  Bell, AlertTriangle, Copy, Package, TrendingUp, Clock, Inbox, Loader2,
  X, CheckCheck, Wallet, ShieldCheck,
} from "lucide-react";

const CAT_META = {
  ocr_anomaly: { icon: AlertTriangle, color: "text-amber-300", bg: "bg-amber-500/10" },
  duplicates:  { icon: Copy,          color: "text-blue-300",  bg: "bg-blue-500/10" },
  low_stock:   { icon: Package,       color: "text-rose-300",  bg: "bg-rose-500/10" },
  price_hike:  { icon: TrendingUp,    color: "text-amber-300", bg: "bg-amber-500/10" },
  trial_ending:{ icon: Clock,         color: "text-violet-300",bg: "bg-violet-500/10" },
  budget:      { icon: Wallet,        color: "text-emerald-300", bg: "bg-emerald-500/10" },
  cert_expiry: { icon: ShieldCheck,   color: "text-rose-300",  bg: "bg-rose-500/10" },
};

const labels = (lang) => ({
  title: lang === "es" ? "Avisos" : "Avisos",
  none: lang === "es" ? "Sin avisos pendientes." : "Sense avisos pendents.",
  view_all: lang === "es" ? "Ver todos" : "Veure tots",
  mark_all_read: lang === "es" ? "Marcar todo leído" : "Marcar tot llegit",
  dismiss: lang === "es" ? "Marcar como leído" : "Marcar com a llegit",
  ocr_anomaly: lang === "es" ? "Anomalías OCR" : "Anomalies OCR",
  duplicates: lang === "es" ? "Duplicados" : "Duplicats",
  low_stock: lang === "es" ? "Stock bajo" : "Estoc baix",
  price_hike: lang === "es" ? "Subida de precios" : "Pujada de preus",
  trial_ending: lang === "es" ? "Fin del trial" : "Fi del trial",
  budget: lang === "es" ? "Presupuesto" : "Pressupost",
  cert_expiry: lang === "es" ? "Certificado caduca" : "Certificat caduca",
});

const fmtDate = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString("ca-ES", { day: "2-digit", month: "short" }); }
  catch { return s; }
};

/**
 * Bell-icon notification center placed in AppShell header.
 * Polls /api/notifications every 60s. Badge shows total count.
 */
export default function NotificationBell() {
  const { lang } = useI18n();
  const L = labels(lang);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], counters: {}, total: 0 });
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications");
      setData(r.data);
    } catch {
      // silent — notifications shouldn't break the app
    } finally { setLoading(false); }
  };

  const dismiss = async (key, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    // Optimistic update — remove from local state immediately
    setData((prev) => {
      const removed = prev.items.find((it) => it.key === key);
      const newItems = prev.items.filter((it) => it.key !== key);
      const newCounters = { ...prev.counters };
      if (removed) newCounters[removed.category] = Math.max(0, (newCounters[removed.category] || 1) - 1);
      return {
        items: newItems,
        counters: newCounters,
        total: Math.max(0, (prev.total || 1) - 1),
      };
    });
    try {
      await api.post("/notifications/dismiss", { keys: [key] });
    } catch {
      // Re-sync if the server rejects (rare)
      fetchAll();
    }
  };

  const dismissAll = async () => {
    setData({ items: [], counters: {}, total: 0 });
    try {
      await api.post("/notifications/dismiss-all", {});
    } catch {
      fetchAll();
    }
  };

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 60_000);
    return () => clearInterval(id);
  }, []);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (!ref.current) return;
      if (open && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const total = data.total || 0;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); if (!open) fetchAll(); }}
        className="relative p-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-colors text-slate-300 hover:text-white"
        data-testid="notification-bell"
        title={L.title}
      >
        <Bell size={16} />
        {total > 0 && (
          <span
            className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold leading-none border-2 border-[#0B1220]"
            data-testid="notification-badge"
          >
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] max-h-[70vh] overflow-hidden flex flex-col bg-[#0B1220] border border-white/10 rounded-xl shadow-2xl z-50"
          data-testid="notifications-panel"
        >
          <div className="p-3 border-b border-white/10 flex items-center justify-between">
            <h3 className="font-semibold text-white text-sm flex items-center gap-2">
              <Inbox size={14} className="text-slate-400" />
              {L.title}
              {total > 0 && (
                <span className="text-[10px] font-mono text-slate-400">{total}</span>
              )}
            </h3>
            <div className="flex items-center gap-2">
              {loading && <Loader2 size={12} className="animate-spin text-slate-500" />}
              {total > 0 && (
                <button
                  type="button"
                  onClick={dismissAll}
                  className="text-[11px] text-slate-400 hover:text-white flex items-center gap-1 px-2 py-1 rounded hover:bg-white/5 transition-colors"
                  data-testid="notifications-dismiss-all"
                >
                  <CheckCheck size={11} />
                  {L.mark_all_read}
                </button>
              )}
            </div>
          </div>
          {/* Category counters strip */}
          {total > 0 && (
            <div className="flex flex-wrap gap-1.5 p-2 border-b border-white/5 text-[10px]">
              {Object.entries(data.counters).filter(([_, n]) => n > 0).map(([k, n]) => (
                <span key={k} className={`px-1.5 py-0.5 rounded font-semibold ${CAT_META[k]?.bg || "bg-slate-500/10"} ${CAT_META[k]?.color || "text-slate-400"}`}>
                  {L[k]} · {n}
                </span>
              ))}
            </div>
          )}
          <div className="overflow-y-auto flex-1">
            {data.items.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">
                {L.none}
              </div>
            ) : (
              data.items.map((it, idx) => {
                const meta = CAT_META[it.category] || {};
                const Icon = meta.icon || AlertTriangle;
                return (
                  <div
                    key={it.key || idx}
                    className="group flex items-start gap-2 p-3 border-b border-white/5 hover:bg-white/[0.03] transition-colors"
                    data-testid={`notification-${it.category}-${idx}`}
                  >
                    <Link
                      to={it.href}
                      onClick={() => setOpen(false)}
                      className="flex items-start gap-2 flex-1 min-w-0"
                    >
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${meta.bg || "bg-slate-500/10"}`}>
                        <Icon size={13} className={meta.color || "text-slate-400"} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white font-medium truncate">
                          {it.title}
                        </div>
                        {it.subtitle && (
                          <div className="text-[11px] text-slate-400 line-clamp-2 mt-0.5">
                            {it.subtitle}
                          </div>
                        )}
                        {it.date && (
                          <div className="text-[10px] text-slate-500 mt-0.5 font-mono">
                            {fmtDate(it.date)}
                          </div>
                        )}
                      </div>
                    </Link>
                    <button
                      type="button"
                      onClick={(e) => dismiss(it.key, e)}
                      className="opacity-0 group-hover:opacity-100 p-1.5 rounded hover:bg-rose-500/20 text-slate-500 hover:text-rose-300 transition-all flex-shrink-0"
                      title={L.dismiss}
                      data-testid={`notification-dismiss-${idx}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
